import { ApiError as ApiErrorSchema, OutlookContext, OutlookMeetingResult } from '@cloudsign/shared';
import type { OutlookMeetingCancel, OutlookMeetingImport } from '@cloudsign/shared';
import type { z } from 'zod';

/**
 * Calls the Outlook endpoints with the add-in's user token (`Authorization: User cst_...`, ADR 0027). The shared `api`
 * client uses the session cookie, which an add-in inside Outlook does not have, so this small client sends the token.
 */
export class OutlookApiError extends Error {
  constructor(
    readonly status: number,
    readonly messageKey: string,
  ) {
    super(messageKey);
  }
}

async function call<S extends z.ZodType>(method: 'GET' | 'POST', path: string, token: string, schema: S, body?: unknown): Promise<z.output<S>> {
  let res: Response;
  try {
    res = await fetch(`/api/v1/pre-registration/outlook${path}`, {
      method,
      headers: { Accept: 'application/json', Authorization: `User ${token}`, ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new OutlookApiError(0, 'error.network');
  }
  const json: unknown = await res.json().catch(() => undefined);
  if (!res.ok) {
    const parsed = ApiErrorSchema.safeParse(json);
    throw new OutlookApiError(res.status, parsed.success ? parsed.data.error.messageKey : 'error.internal');
  }
  return schema.parse(json);
}

export const getOutlookContext = (token: string) => call('GET', '/context', token, OutlookContext);
export const importMeeting = (token: string, body: z.input<typeof OutlookMeetingImport>) => call('POST', '/meeting', token, OutlookMeetingResult, body);
export const cancelMeeting = (token: string, body: z.input<typeof OutlookMeetingCancel>) => call('POST', '/meeting/cancel', token, OutlookMeetingResult, body);
